import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Opportunity as ApiOpportunity } from '../../../../../js/api/models/Opportunity';
import { AssignmentsService } from '../../../../../js/api/services/AssignmentsService';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Button } from '../../../components/button';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { PageHeader } from '../../../components/PageHeader';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useDebounce } from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import OpportunityCard from './OpportunityCard';
import { OpportunityDrawer } from './OpportunityDrawer';
import OpportunityFilters from './OpportunityFilters';
import { OPPORTUNITIES_LIST_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 12; // Number of items per page

const OpportunityList: React.FC = () => {
  useDocumentTitle('Opportunities');

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Degree',
    'Specialty',
    'ShiftRateTypes',
    'ShiftRateTypeIcons',
  ]);

  // Filter state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('-');
  const [showClosed, setShowClosed] = useState(false);

  // Drawer state
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Build query key with filters
  const queryKey = useMemo(
    () => [
      OPPORTUNITIES_LIST_QUERY_KEY,
      'list',
      {
        search: debouncedSearch || undefined,
        sortBy,
        sortDirection,
        showClosed,
      },
    ],
    [debouncedSearch, sortBy, sortDirection, showClosed]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      const ordering = sortDirection && sortBy ? `${sortDirection}${sortBy}` : sortBy;
      return await AssignmentsService.assignmentsOpportunitiesList({
        search: debouncedSearch || undefined,
        ordering: ordering || undefined,
        showClosed: showClosed,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
  });

  const opportunities = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  // Set up infinite scroll
  const loadMoreOpportunities = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreOpportunities, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  // Check URL for opportunity ID and open drawer if present
  useEffect(() => {
    const opportunityIdFromUrl = searchParams.get('id');
    if (opportunityIdFromUrl) {
      setSelectedOpportunityId(opportunityIdFromUrl);
      setIsDrawerOpen(true);
    } else {
      // URL param was removed, close drawer only if it was open
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
        setSelectedOpportunityId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleResetList = () => {
    setSearch('');
    setSortBy('created_at');
    setSortDirection('-');
    setShowClosed(false);
  };

  const handleCreateOpportunity = () => {
    navigate('/app/organization/opportunities/new');
  };

  const handleOpportunityCardClick = (opportunity: ApiOpportunity) => {
    setSelectedOpportunityId(opportunity.id);
    setIsDrawerOpen(true);
    // Update URL to include opportunity ID
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('id', opportunity.id);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedOpportunityId(null);
    // Remove opportunity ID from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        {/* Main content area - takes 2/3 when drawer open, full width when closed */}
        <div
          className={clsx(
            'tw:flex tw:flex-col tw:transition-all tw:duration-300 tw:overflow-hidden',
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <PageHeader
            backUrl="/app"
            title="Opportunities"
            buttonHtml={
              <Button color="brand-green-outline-on-dark" onClick={handleCreateOpportunity}>
                Create Opportunity
              </Button>
            }
          />

          <main className="tw:flex-1 tw:overflow-y-auto tw:max-w-7xl tw:mx-auto tw:mt-3 tw:px-6 tw:w-full">
            <div className="tw:flex tw:flex-col tw:gap-3">
              <OpportunityFilters
                search={search}
                onSearchChange={setSearch}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                sortDirection={sortDirection}
                onSortDirectionChange={setSortDirection}
                showClosed={showClosed}
                onShowClosedChange={setShowClosed}
                onReset={handleResetList}
              />
            </div>
            <div className="tw:mt-3">
              {isLoading && opportunities.length === 0 && (
                <div className="tw:text-center tw:py-8">Loading opportunities...</div>
              )}
              {!isLoading && opportunities.length === 0 ? (
                <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
                  {search
                    ? 'No opportunities found matching your search.'
                    : 'No opportunities found.'}
                </div>
              ) : (
                <div className="tw:@container">
                  <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                    {(() => {
                      const opportunityIdFromUrl = searchParams.get('id');
                      return opportunities.map((opportunity) => {
                        const isSelected = opportunity.id === opportunityIdFromUrl;
                        return (
                          <div
                            key={opportunity.id}
                            onClick={() => handleOpportunityCardClick(opportunity)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleOpportunityCardClick(opportunity);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <OpportunityCard
                              opportunity={opportunity}
                              degreeMap={codeDefinitionMaps?.Degree || {}}
                              specialtyMap={codeDefinitionMaps?.Specialty || {}}
                              shiftRateTypeMap={codeDefinitionMaps?.ShiftRateTypes || {}}
                              shiftRateTypeIconMap={codeDefinitionMaps?.ShiftRateTypeIcons || {}}
                              isSelected={isSelected}
                            />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Infinite scroll loading indicator */}
              {hasNextPage && (
                <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                  {isFetchingNextPage && (
                    <div className="tw:text-center tw:text-zinc-500">
                      Loading more opportunities...
                    </div>
                  )}
                </div>
              )}

              {/* Show total count when there are results */}
              {opportunities.length > 0 && (
                <div className="tw:text-center tw:text-zinc-500 tw:mt-4">
                  Showing {opportunities.length} of {totalCount} opportunities
                </div>
              )}
            </div>
          </main>
        </div>

        {/* Drawer - side-by-side on desktop, modal on mobile */}
        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedOpportunityId && (
              <OpportunityDrawer
                key={selectedOpportunityId}
                opportunityId={selectedOpportunityId}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};

export default OpportunityList;
